import { html, css } from 'lit-element';
import { connect } from '../core/connect';

import { withProduct } from '../core/resolveProperties';
import { TemplateElement } from '../core/base';
import {
  makeDiscountedProductPriceSelector,
  makeProductDefaultFrequencySelector,
  makeProductFrequencyOptedInSelector
} from '../core/selectors';
import { safeProductId } from '../core/utils';

export class Price extends withProduct(TemplateElement) {
  static get properties() {
    return {
      ...super.properties,
      regular: { type: Boolean, reflect: true },
      subscription: { type: Boolean, reflect: true },
      discount: { type: Boolean, reflect: true },
      /** Force displaying the pay-as-you-go price. This is relevant when there is a prepaid plan that the user has opted into, and you still want to display the pay-as-you-go price for comparison */
      payAsYouGo: { type: Boolean, reflect: true, attribute: 'pay-as-you-go' },
      frequency: { type: Object },
      /** If Shopify, this is derived from the selling plans attached to the product */
      productPlans: { type: Object },
      /** The discounted price, as calculated from the Offers API response */
      discountedProductPriceFromOffers: { type: Object }
    };
  }

  static get styles() {
    return css`
      :host::before {
        clip-path: inset(100%);
        clip: rect(1px, 1px, 1px, 1px);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
      }

      :host([subscription])::before {
        content: 'Discounted subscription price';
      }

      :host([regular])::before {
        content: 'Regular price';
      }
    `;
  }

  get value() {
    const realProductId = safeProductId(this.product);
    const frequency = this.frequency || this.configDefaultFrequency || this.offer?.defaultFrequency;
    const plans = this.productPlans[realProductId] || [];

    let currentPlan = this.payAsYouGo
      ? plans.find(plan => plan.prepaidShipments === null || plan.prepaidShipments === undefined)
      : plans.find(plan => plan.frequency === frequency);

    if (!currentPlan) return '';

    // default to pulling from the selling plan
    let { regularPrice, discountRate, subscriptionPrice } = currentPlan;
    // if the selling plan has no price adjustments, then use the offer response to determine the discounted price
    // this will be true for merchants on standardized offer profiles
    // we still rely on the selling plan for prepaid subscriptions, for simplicity
    if (
      // overrideSellingPlanPrice is a dev flag to force using the offer price for testing purposes
      (currentPlan.hasPriceAdjustments === false || this.offer?.overrideSellingPlanPrice) &&
      !currentPlan.prepaidShipments
    ) {
      ({ regularPrice, discountRate, subscriptionPrice } = this.discountedProductPriceFromOffers);
    }

    // if payAsYouGo, always show the price even if no discount
    // it's unclear if this was the original intention, but preserving existing behavior
    if (subscriptionPrice === regularPrice && !this.payAsYouGo) return '';

    if (this.regular) {
      return regularPrice;
    }
    if (this.discount) return discountRate;
    return subscriptionPrice;
  }

  render() {
    const value = this.value;
    if (value)
      return html`
        <slot name="prepend"></slot>
        ${value}
        <slot name="append"></slot>
      `;

    return html`
      <slot name="fallback"></slot>
    `;
  }
}
const mapStateToProps = (state, ownProps) => ({
  productPlans: state.productPlans,
  configDefaultFrequency: makeProductDefaultFrequencySelector(ownProps.product?.id)(state),
  frequency: makeProductFrequencyOptedInSelector(ownProps.product)(state),
  discountedProductPriceFromOffers: makeDiscountedProductPriceSelector(ownProps.product?.id)(state)
});

export default connect(mapStateToProps)(Price);
